import {
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import type {
    ChangeEvent
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    Platform,
    Linking,
    Image,
    Modal,
    View
} from "react-native";
import type {
    DriveFolderPayloadType,
    DriveFilePayloadType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    useDriveUpload
} from "../../../../contexts/driveUpload";
import {
    FileSpreadsheetIcon,
    FolderOutputIcon,
    FileArchiveIcon,
    FolderPlusIcon,
    ArrowLeftIcon,
    FileAudioIcon,
    FileImageIcon,
    FileVideoIcon,
    FolderUpIcon,
    FileTextIcon,
    FileCodeIcon,
    PencilIcon,
    UploadIcon,
    FolderIcon,
    EyeOffIcon,
    TrashIcon,
    FileIcon,
    LinkIcon,
    EyeIcon,
    XIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Clipboard from "@react-native-clipboard/clipboard";
import {
    SERVER_ADDRESS
} from "../../../../services/restAPI/constants";
import DriveFolderSelectorModal from "../../../../components/driveFolderSelectorModal";
import RESTServiceContext from "../../../../services/restAPI/context";
import Pagination from "../../../../components/pagination";

const Drive = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        currentFolderID,
        setCurrentFolderID
    ] = useState<string | undefined>(undefined);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        folderHistory,
        setFolderHistory
    ] = useState<{ id: string | undefined; name: string }[]>([
        {
            id: undefined,
            name: "Drive"
        }
    ]);

    const [
        folders,
        setFolders
    ] = useState<DriveFolderPayloadType[]>([]);

    const [
        files,
        setFiles
    ] = useState<DriveFilePayloadType[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        isCreateFolderModalOpen,
        setIsCreateFolderModalOpen
    ] = useState<boolean>(false);

    const [
        newFolderName,
        setNewFolderName
    ] = useState<string>("");

    const [
        isDeleteModalOpen,
        setIsDeleteModalOpen
    ] = useState<boolean>(false);

    const [
        itemToDelete,
        setItemToDelete
    ] = useState<{ id: string; type: "folder" | "file" } | null>(null);

    const [
        isPreviewModalOpen,
        setIsPreviewModalOpen
    ] = useState<boolean>(false);

    const [
        previewUrl,
        setPreviewUrl
    ] = useState<string>("");

    const [
        isRenameModalOpen,
        setIsRenameModalOpen
    ] = useState<boolean>(false);

    const [
        itemToRename,
        setItemToRename
    ] = useState<{ id: string; type: "folder" | "file"; currentName: string } | null>(null);

    const [
        isMoveModalOpen,
        setIsMoveModalOpen
    ] = useState<boolean>(false);

    const [
        itemToMove,
        setItemToMove
    ] = useState<{ id: string; type: "folder" | "file" } | null>(null);

    const [
        authToken,
        setAuthToken
    ] = useState<string>("");

    const [
        newName,
        setNewName
    ] = useState<string>("");

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        debouncedSearchQuery,
        setDebouncedSearchQuery
    ] = useState<string>("");

    const [
        isFolderPreparing,
        setIsFolderPreparing
    ] = useState<boolean>(false);

    const folderInputRef = useRef<HTMLInputElement>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        subscribeToCompletion,
        uploadFolder,
        uploadFiles
    } = useDriveUpload();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchDriveItems = useCallback(async (folderID: string | undefined, currentPage: number, search: string = "") => {
        try {
            setLoading(true);

            const fileParams: { folderID?: string; page: number; search?: string } = {
                page: currentPage
            };
            if(folderID) {
                fileParams.folderID = folderID;
            }
            // Use the new searchQuery state if it exists
            if(search.trim()) {
                fileParams.search = search.trim();
            }

            const itemsResult = await RESTServiceContext.action("getDriveItems", fileParams);

            if(itemsResult.data && itemsResult.data.data) {
                const fetchedItems = itemsResult.data.data;
                const newFolders = fetchedItems.filter((i: unknown) => (i as Record<string, unknown>).itemType === "folder") as unknown as DriveFolderPayloadType[];
                const newFiles = fetchedItems.filter((i: unknown) => (i as Record<string, unknown>).itemType === "file").map((i: unknown) => ({
                    ...(i as Record<string, unknown>),
                    fileName: (i as Record<string, unknown>).name
                })) as unknown as DriveFilePayloadType[];

                setFolders(newFolders);
                setFiles(newFiles);

                if (itemsResult.data.totalDataCount !== undefined) {
                    setTotalCount(itemsResult.data.totalDataCount);
                }
            } else {
                setFolders([]);
                setFiles([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => clearTimeout(handler);
    }, [
        searchQuery
    ]);

    useEffect(() => {
        fetchDriveItems(currentFolderID, 1, debouncedSearchQuery);
        setPage(1);
    }, [
        debouncedSearchQuery,
        currentFolderID,
        fetchDriveItems
    ]);

    useEffect(() => {
        AsyncStorage.getItem("authToken").then((token: string | null) => {
            if (token) {
                setAuthToken(token);
            }
        });
    }, [
        currentFolderID,
        fetchDriveItems
    ]);

    const currentFolderIDRef = useRef<string | null>(currentFolderID);
    currentFolderIDRef.current = currentFolderID;

    const pageRef = useRef<number>(page);
    pageRef.current = page;

    useEffect(() => {
        return subscribeToCompletion((completedItem) => {
            if(completedItem.folderID === (currentFolderIDRef.current || undefined)) {
                fetchDriveItems(currentFolderIDRef.current || undefined, pageRef.current);
            }
        });
    }, [
        subscribeToCompletion,
        fetchDriveItems
    ]);

    const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if(!fileList || fileList.length === 0) {
            return;
        }

        const filesArray = Array.from(fileList);
        uploadFiles(filesArray, currentFolderID || undefined);

        if(fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleFolderUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if(!fileList || fileList.length === 0) {
            return;
        }

        const filesArray = Array.from(fileList);

        try {
            setIsFolderPreparing(true);
            await uploadFolder(filesArray, currentFolderID || undefined);
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("something-went-wrong") || "Klasör yüklenirken hata oluştu."
            });
        } finally {
            setIsFolderPreparing(false);
            if(folderInputRef.current) {
                folderInputRef.current.value = "";
            }
        }
    };

    const handleCreateFolder = async () => {
        if(!newFolderName.trim()) return;
        try {
            await RESTServiceContext.action("createDriveFolder", {
                parentFolderID: currentFolderID || undefined,
                name: newFolderName,
                isPublic: true
            });
            setNewFolderName("");
            setIsCreateFolderModalOpen(false);
            await fetchDriveItems(currentFolderID, page);
            NCoreUIKitSnackBar.open({
                title: localize("drive-folder-created-success") || "Klasör başarıyla oluşturuldu."
            });
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("drive-folder-created-error") || "Klasör oluşturulurken hata oluştu."
            });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        try {
            if (itemToDelete.type === "folder") {
                await RESTServiceContext.action("deleteDriveFolder", {
                    folderID: itemToDelete.id
                });
            } else {
                await RESTServiceContext.action("deleteDriveFile", {
                    fileID: itemToDelete.id
                });
            }
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
            await fetchDriveItems(currentFolderID, page);
            NCoreUIKitSnackBar.open({
                title: localize("drive-delete-success") || "Başarıyla silindi."
            });
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("drive-delete-error") || "Silinirken hata oluştu."
            });
        }
    };

    const handleRenameConfirm = async () => {
        if (!itemToRename || !newName.trim()) return;
        try {
            if (itemToRename.type === "folder") {
                await RESTServiceContext.action("updateDriveFolder", {
                    folderID: itemToRename.id,
                    name: newName
                });
            } else {
                let finalName = newName;
                const currentName = itemToRename.currentName;
                const dotIndex = currentName.lastIndexOf(".");
                if (dotIndex !== -1) {
                    const ext = currentName.substring(dotIndex);
                    if (!finalName.endsWith(ext)) {
                        finalName += ext;
                    }
                }

                await RESTServiceContext.action("updateDriveFile", {
                    fileID: itemToRename.id,
                    fileName: finalName
                });
            }
            setIsRenameModalOpen(false);
            setItemToRename(null);
            setNewName("");
            await fetchDriveItems(currentFolderID, page);
            NCoreUIKitSnackBar.open({
                title: localize("drive-rename-success") || "Başarıyla yeniden adlandırıldı."
            });
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("drive-rename-error") || "Yeniden adlandırılırken hata oluştu."
            });
        }
    };

    const handleMoveItem = async (targetFolderID: string | undefined) => {
        if (!itemToMove) return;
        try {
            if (itemToMove.type === "folder") {
                await RESTServiceContext.action("updateDriveFolder", {
                    parentFolderID: targetFolderID || null as unknown as string,
                    folderID: itemToMove.id
                });
            } else {
                await RESTServiceContext.action("updateDriveFile", {
                    folderID: targetFolderID || null as unknown as string,
                    fileID: itemToMove.id
                });
            }
            setIsMoveModalOpen(false);
            setItemToMove(null);
            await fetchDriveItems(currentFolderID, page);
            NCoreUIKitSnackBar.open({
                title: "Başarıyla taşındı.",
                type: "success"
            });
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: "Taşınırken hata oluştu."
            });
        }
    };

    const handleNavigateFolder = (folderID: string, name: string) => {
        setFolderHistory(prev => [
            ...prev,
            {
                id: folderID,
                name
            }
        ]);
        setCurrentFolderID(folderID);
    };

    const handleGoBack = () => {
        if(folderHistory.length > 1) {
            const newHistory = [...folderHistory];
            newHistory.pop();
            setFolderHistory(newHistory);
            if (newHistory.length > 0) {
                const lastItem = newHistory[newHistory.length - 1];
                if (lastItem) {
                    setCurrentFolderID(lastItem.id);
                }
            }
        }
    };

    const handleToggleShare = async (item: DriveFilePayloadType | DriveFolderPayloadType, type: "file" | "folder") => {
        try {
            setLoading(true);
            if (type === "folder") {
                await RESTServiceContext.action("updateDriveFolder", {
                    isPublic: !item.isPublic,
                    folderID: item._id
                });
            } else {
                await RESTServiceContext.action("updateDriveFile", {
                    isPublic: !item.isPublic,
                    fileID: item._id
                });
            }
            NCoreUIKitSnackBar.open({
                title: localize("drive-share-status-updated") || "Paylaşım durumu güncellendi",
                type: "success"
            });
            fetchDriveItems(currentFolderID, page);
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("drive-share-status-error") || "Paylaşım durumu güncellenemedi",
                type: "danger"
            });
        } finally {
            setLoading(false);
        }
    };

    const renderHeader = () => {
        const lastFolder = folderHistory[folderHistory.length - 1];
        const currentFolderName = lastFolder ? lastFolder.name : "Sürücü";

        return <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerTitleContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    folderHistory.length > 1 ?
                        <Button
                            icon={() => <ArrowLeftIcon
                                color={colors.project.brandWhite}
                                size={24}
                            />}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            spreadBehaviour="free"
                            onPress={handleGoBack}
                            variant="filled"
                        />
                    :
                        null
                }
                <Text
                    variant="titleLargeSize"
                >
                    {currentFolderName}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.headerActions,
                    {
                        width: isMobile ? "100%" : "auto",
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.searchContainer,
                        isMobile && {
                            flexBasis: "100%"
                        }
                    ]}
                >
                    <TextInput
                        placeholder={localize("search") || "Ara..."}
                        customColor={colors.project.brandWhite}
                        onChangeText={setSearchQuery}
                        spreadBehaviour="stretch"
                        value={searchQuery}
                    />
                </View>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        flexBasis: isMobile ? "100%" : "auto",
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={{
                            width: isMobile ? "100%" : "auto"
                        }}
                    >
                        <Button
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            onPress={() => fileInputRef.current?.click()}
                            title={localize("drive-upload-file-button")}
                            customTextColor={colors.project.brandWhite}
                            icon={({
                                size
                            }) => <UploadIcon
                                color={colors.project.brandWhite}
                                size={size}
                            />}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            variant="outline"
                        />
                    </View>
                    <View
                        style={{
                            width: isMobile ? "100%" : "auto"
                        }}
                    >
                        <Button
                            title={localize("drive-upload-folder-button") || "Klasör Yükle"}
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            onPress={() => folderInputRef.current?.click()}
                            customTextColor={colors.project.brandWhite}
                            icon={({
                                size
                            }) => <FolderUpIcon
                                color={colors.project.brandWhite}
                                size={size}
                            />}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            isLoading={isFolderPreparing}
                            variant="outline"
                        />
                    </View>
                    <View
                        style={{
                            width: isMobile ? "100%" : "auto"
                        }}
                    >
                        <Button
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            title={localize("drive-create-folder-button")}
                            customTextColor={colors.project.brandWhite}
                            icon={({
                                color,
                                size
                            }) => <FolderPlusIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => {
                                setIsCreateFolderModalOpen(true);
                            }}
                            customColor="gold"
                        />
                    </View>
                </View>
            </View>
        </View>;
    };

    const renderFolder = (folder: DriveFolderPayloadType, index: number) => {
        return <View
            key={folder._id || `folder-${index}`}
            style={stylesheet.itemWrapper}
        >
            <TouchableOpacity
                onPress={() => handleNavigateFolder(folder._id, folder.name)}
                style={[
                    stylesheet.itemContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg,
                        gap: spaces.spacingSm
                    }
                ]}
                activeOpacity={0.7}
            >
                <View
                    style={[
                        stylesheet.actionButtonsContainer,
                        {
                            right: spaces.spacingSm,
                            gap: spaces.spacingMd,
                            top: spaces.spacingSm
                        }
                    ]}
                >
                    <TouchableOpacity
                        onPress={() => {
                            setItemToMove({
                                id: folder._id,
                                type: "folder"
                            });
                            setIsMoveModalOpen(true);
                        }}
                    >
                        <FolderOutputIcon
                            color={colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => {
                            Clipboard.setString(folder._id);
                            NCoreUIKitSnackBar.open({
                                title: localize("link-copied") || "Bağlantı kopyalandı",
                                type: "success"
                            });
                        }}
                    >
                        <LinkIcon
                            color={colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => handleToggleShare(folder, "folder")}
                    >
                        {
                            folder.isPublic ?
                                <EyeIcon
                                    color={colors.content.text.high}
                                    size={20}
                                />
                            :
                                <EyeOffIcon
                                    color={colors.content.icon.mid}
                                    size={20}
                                />
                        }
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => {
                            setItemToRename({
                                currentName: folder.name,
                                id: folder._id,
                                type: "folder"
                            });
                            setNewName(folder.name);
                            setIsRenameModalOpen(true);
                        }}
                    >
                        <PencilIcon
                            color={colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => {
                            setItemToDelete({
                                id: folder._id,
                                type: "folder"
                            });
                            setIsDeleteModalOpen(true);
                        }}
                    >
                        <TrashIcon
                            color={colors.content.text.danger}
                            size={20}
                        />
                    </TouchableOpacity>
                </View>
                <FolderIcon
                    color={colors.content.icon.mid}
                    size={160}
                />
                <Text
                    style={stylesheet.itemTitle}
                    variant="bodyMediumSize"
                    numberOfLines={1}
                >
                    {folder.name}
                </Text>
            </TouchableOpacity>
        </View>;
    };

    const renderFile = (file: DriveFilePayloadType, index: number) => {
        const isImage = file.fileType.startsWith("image/");
        const parsedFilePath = file.filePath.startsWith("/") ? `${SERVER_ADDRESS}${file.filePath}` : file.filePath;
        const encodedFilePath = encodeURI(parsedFilePath);
        const tokenQuery = encodedFilePath.includes("?") ? `&token=${authToken}` : `?token=${authToken}`;
        const fileUrl = file.isPublic ? encodedFilePath : `${encodedFilePath}${tokenQuery}`;

        let FileIconComponent = FileIcon;
        const ft = file.fileType.toLowerCase();

        if (ft.includes("pdf")) {
            FileIconComponent = FileTextIcon;
        } else if (ft.includes("sheet") || ft.includes("excel") || ft.includes("csv")) {
            FileIconComponent = FileSpreadsheetIcon;
        } else if (ft.includes("video")) {
            FileIconComponent = FileVideoIcon;
        } else if (ft.includes("audio")) {
            FileIconComponent = FileAudioIcon;
        } else if (ft.includes("zip") || ft.includes("rar") || ft.includes("archive") || ft.includes("tar")) {
            FileIconComponent = FileArchiveIcon;
        } else if (ft.includes("text") || ft.includes("document") || ft.includes("word")) {
            FileIconComponent = FileTextIcon;
        } else if (ft.includes("json") || ft.includes("xml") || ft.includes("html") || ft.includes("javascript")) {
            FileIconComponent = FileCodeIcon;
        } else if (ft.includes("image")) {
            FileIconComponent = FileImageIcon;
        }

        return <View
            key={file._id || `file-${index}`}
            style={stylesheet.itemWrapper}
        >
            <TouchableOpacity
                style={[
                    stylesheet.itemContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg,
                        gap: spaces.spacingSm
                    }
                ]}
                onPress={() => {
                    if (isImage) {
                        setPreviewUrl(fileUrl);
                        setIsPreviewModalOpen(true);
                    } else {
                        Linking.openURL(fileUrl);
                    }
                }}
                activeOpacity={0.7}
            >
                <View
                    style={[
                        stylesheet.actionButtonsContainer,
                        {
                            right: spaces.spacingSm,
                            gap: spaces.spacingMd,
                            top: spaces.spacingSm
                        }
                    ]}
                >
                    <TouchableOpacity
                        onPress={() => {
                            setItemToMove({
                                id: file._id,
                                type: "file"
                            });
                            setIsMoveModalOpen(true);
                        }}
                    >
                        <FolderOutputIcon
                            color={colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => {
                            Clipboard.setString(fileUrl);
                            NCoreUIKitSnackBar.open({
                                title: localize("link-copied") || "Bağlantı kopyalandı",
                                type: "success"
                            });
                        }}
                    >
                        <LinkIcon
                            color={colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => handleToggleShare(file, "file")}
                    >
                        {
                            file.isPublic ?
                                <EyeIcon
                                    color={colors.content.text.high}
                                    size={20}
                                />
                            :
                                <EyeOffIcon
                                    color={colors.content.icon.mid}
                                    size={20}
                                />
                        }
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => {
                            setItemToRename({
                                currentName: file.fileName,
                                id: file._id,
                                type: "file"
                            });
                            const dotIndex = file.fileName.lastIndexOf(".");
                            if (dotIndex !== -1) {
                                setNewName(file.fileName.substring(0, dotIndex));
                            } else {
                                setNewName(file.fileName);
                            }
                            setIsRenameModalOpen(true);
                        }}
                    >
                        <PencilIcon
                            color={colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => {
                            setItemToDelete({
                                id: file._id,
                                type: "file"
                            });
                            setIsDeleteModalOpen(true);
                        }}
                    >
                        <TrashIcon
                            color={colors.content.text.danger}
                            size={20}
                        />
                    </TouchableOpacity>
                </View>
                {
                    isImage ?
                        <Image
                            style={[
                                stylesheet.imagePreview,
                                {
                                    borderRadius: radiuses.md
                                }
                            ]}
                            resizeMode="cover"
                            source={{
                                uri: fileUrl
                            }}
                        />
                    :
                        <FileIconComponent
                            color={colors.content.icon.mid}
                            size={160}
                        />
                }
                <Text
                    style={stylesheet.itemTitle}
                    variant="bodyMediumSize"
                    numberOfLines={1}
                >
                    {file.fileName || "İsimsiz Dosya"}
                </Text>
            </TouchableOpacity>
        </View>;
    };

    const renderPagination = () => {
        if (totalCount === 0) {
            return null;
        }

        return <View
            style={[
                stylesheet.paginationContainer,
                {
                    marginTop: spaces.spacingLg
                }
            ]}
        >
            <Pagination
                onSelect={(item) => {
                    setPage(item.pageNumber);
                    fetchDriveItems(currentFolderID, item.pageNumber);
                }}
                totalDataCount={totalCount}
                currentPage={page}
                itemPerPage={20}
            />
        </View>;
    };

    const renderContent = () => {
        if(loading && folders.length === 0 && files.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if(folders.length === 0 && files.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    style={[
                        stylesheet.emptyText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyLargeSize"
                >
                    {localize("drive-empty-folder")}
                </Text>
            </View>;
        }

        return <View>
            <View
                style={[
                    stylesheet.gridContainer,
                    {
                        marginTop: spaces.spacingLg,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {folders.map(renderFolder)}
                {files.map(renderFile)}
            </View>
            {renderPagination()}
        </View>;
    };

    const renderRenameModal = () => {
        return <Dialog
            title={localize("drive-rename-title") || "Yeniden Adlandır"}
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "Kapat",
                onPress: () => {
                    setIsRenameModalOpen(false);
                    setItemToRename(null);
                    setNewName("");
                },
                customColor: "secondaryButton",
                variant: "filled"
            }}
            primaryButtonProps={{
                title: localize("save") || "Kaydet",
                onPress: handleRenameConfirm
            }}
            onClosed={() => {
                setIsRenameModalOpen(false);
                setItemToRename(null);
                setNewName("");
            }}
            onOverlayPress={() => {
                setIsRenameModalOpen(false);
                setItemToRename(null);
                setNewName("");
            }}
            isVisible={isRenameModalOpen}
            variant="yes-no"
        >
            <View
                style={stylesheet.modalContentContainer}
            >
                <TextInput
                    placeholder={localize("drive-rename-placeholder") || "Yeni Ad"}
                    customColor={colors.project.brandWhite}
                    spreadBehaviour="stretch"
                    onChangeText={setNewName}
                    value={newName}
                />
            </View>
        </Dialog>;
    };

    const renderCreateFolderModal = () => {
        return <Dialog
            title={localize("drive-create-folder-button") || "Yeni Klasör"}
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                onPress: () => {
                    setIsCreateFolderModalOpen(false);
                    setNewFolderName("");
                },
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "Kapat",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            primaryButtonProps={{
                title: localize("save") || "Kaydet",
                onPress: handleCreateFolder
            }}
            onClosed={() => {
                setIsCreateFolderModalOpen(false);
                setNewFolderName("");
            }}
            onOverlayPress={() => {
                setIsCreateFolderModalOpen(false);
                setNewFolderName("");
            }}
            isVisible={isCreateFolderModalOpen}
            variant="yes-no"
        >
            <View
                style={stylesheet.modalContentContainer}
            >
                <TextInput
                    placeholder={localize("drive-folder-name-placeholder") || "Klasör Adı"}
                    customColor={colors.project.inputBackground}
                    onChangeText={setNewFolderName}
                    spreadBehaviour="stretch"
                    value={newFolderName}
                />
            </View>
        </Dialog>;
    };

    return <PageContainer
        isScrollable={true}
    >
        {Platform.OS === "web" && (<input
            onChange={handleFileUpload}
            style={{
                display: "none"
            }}
            ref={fileInputRef}
            multiple={true}
            type="file"
        />)}
        {Platform.OS === "web" && (<input
            ref={(el) => {
                folderInputRef.current = el;
                if(el) {
                    el.setAttribute("webkitdirectory", "");
                    el.setAttribute("directory", "");
                }
            }}
            onChange={handleFolderUpload}
            style={{
                display: "none"
            }}
            multiple={true}
            type="file"
        />)}
        {renderCreateFolderModal()}
        <Dialog
            primaryButtonProps={{
                icon: ({
                    color,
                    size
                }) => <TrashIcon
                    color={colors.content.icon[color as never] || color}
                    size={size}
                />,
                title: localize("delete") || "Sil",
                onPress: handleDeleteConfirm,
                type: "danger"
            }}
            title={localize("drive-delete-title") || "Sil"}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                onPress: () => {
                    setIsDeleteModalOpen(false);
                    setItemToDelete(null);
                },
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => {
                setIsDeleteModalOpen(false);
                setItemToDelete(null);
            }}
            onOverlayPress={() => {
                setIsDeleteModalOpen(false);
                setItemToDelete(null);
            }}
            isVisible={isDeleteModalOpen}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("drive-delete-desc") || "Bu öğeyi silmek istediğinize emin misiniz?"}
                </Text>
            </View>
        </Dialog>
        <Modal
            onRequestClose={() => setIsPreviewModalOpen(false)}
            visible={isPreviewModalOpen}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.content.container.overlay
                    }
                ]}
            >
                <TouchableOpacity
                    onPress={() => setIsPreviewModalOpen(false)}
                    style={[
                        stylesheet.lightboxCloseButton,
                        {
                            right: spaces.spacingLg,
                            top: spaces.spacingLg
                        }
                    ]}
                >
                    <XIcon
                        color={colors.project.brandWhite}
                        size={32}
                    />
                </TouchableOpacity>
                {
                    previewUrl ?
                        <Image
                            style={stylesheet.previewImage}
                            resizeMode="contain"
                            source={{
                                uri: previewUrl
                            }}
                        />
                    :
                        null
                }
            </View>
        </Modal>
        <DriveFolderSelectorModal
            onClose={() => setIsMoveModalOpen(false)}
            isVisible={isMoveModalOpen}
            onMove={handleMoveItem}
        />
        {renderRenameModal()}
        <View
            style={{
                padding: spaces.spacingXl
            }}
        >
            {renderHeader()}
            {renderContent()}
        </View>
    </PageContainer>;
};

export default Drive;
